"use client";

import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";
import { useAnalytics } from "@/lib/analytics/hooks";
import type { SimilarJob } from "@/lib/db/queries/jobs";
import { jobLocationDisplay } from "@/lib/jobs/location-display";
import { buildJobUrl } from "@/lib/sitemap/utils";
import { formatPayRange } from "@/lib/utils/format";

type Props = {
	jobs: SimilarJob[];
	locale: string;
	currentJobId: string;
	/**
	 * "rail" (default): the full rail near the page end, anchor `#related-jobs`.
	 * "next_step": compact block right after the job description for readers
	 * who decide "not this one".
	 */
	placement?: "rail" | "next_step";
	limit?: number;
};

const NEXT_STEP_DEFAULT_LIMIT = 3;

export function RelatedJobs({
	jobs,
	locale,
	currentJobId,
	placement = "rail",
	limit,
}: Props) {
	const { trackJobClick } = useAnalytics();
	const posthog = usePostHog();
	const sectionRef = useRef<HTMLElement>(null);
	const isNextStep = placement === "next_step";
	const cap = limit ?? NEXT_STEP_DEFAULT_LIMIT;
	const shown = isNextStep ? jobs.slice(0, cap) : jobs;
	const shownCount = shown.length;

	useEffect(() => {
		if (!isNextStep || shownCount === 0) return;
		const el = sectionRef.current;
		if (!el || typeof IntersectionObserver === "undefined") return;
		const obs = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					posthog?.capture("related_jobs_shown", {
						placement,
						count: shownCount,
						source_job_id: currentJobId,
					});
					obs.disconnect();
				}
			},
			{ threshold: 0.5 },
		);
		obs.observe(el);
		return () => obs.disconnect();
	}, [isNextStep, shownCount, placement, currentJobId, posthog]);

	if (jobs.length === 0) return null;

	const linkRing =
		"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

	const headerLink = isNextStep ? (
		jobs.length > cap ? (
			<Link
				href="#related-jobs"
				className={`rounded-sm text-sm font-medium text-primary hover:underline ${linkRing}`}
			>
				{locale === "en"
					? `See all ${jobs.length} similar →`
					: `Lihat ${jobs.length} lowongan serupa →`}
			</Link>
		) : (
			<Link
				href={`/${locale}/jobs`}
				className={`rounded-sm text-sm font-medium text-primary hover:underline ${linkRing}`}
			>
				{locale === "en" ? "View all →" : "Lihat semua →"}
			</Link>
		)
	) : (
		<Link
			href={`/${locale}/jobs`}
			className={`rounded-sm text-sm font-medium text-primary hover:underline ${linkRing}`}
		>
			{locale === "en" ? "View all →" : "Lihat semua →"}
		</Link>
	);

	return (
		<section
			ref={sectionRef}
			{...(isNextStep
				? {
						"data-testid": "job-next-step",
						"aria-labelledby": "job-next-step-heading",
					}
				: { id: "related-jobs" })}
			className={
				isNextStep
					? "space-y-3 rounded-2xl border border-border/60 bg-muted/40 p-4 sm:p-5"
					: "space-y-4 border-t border-border/40 pt-8"
			}
		>
			<div className="flex items-center justify-between gap-4">
				<h2
					id={isNextStep ? "job-next-step-heading" : undefined}
					className="text-lg font-semibold text-foreground"
				>
					{isNextStep
						? locale === "en"
							? "Not quite right? Similar roles hiring now"
							: "Kurang cocok? Lowongan serupa yang sedang buka"
						: locale === "en"
							? "Similar jobs you might like"
							: "Lowongan serupa yang mungkin cocok"}
				</h2>
				{headerLink}
			</div>
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
				{shown.map((sj, index) => {
					const pay = formatPayRange(
						sj.payMin,
						sj.payMax,
						sj.payCurrency,
						sj.payPeriod,
					);
					const href = buildJobUrl({
						jobId: sj.id,
						title: sj.title,
						company: sj.company,
						locale,
					});
					// Shared helper — reads the stored geo_fit/applicant_region columns
					// this call site used to omit, so a related-jobs card can no longer
					// promise "Open worldwide" for a job whose own page says the
					// employer never stated who may apply.
					const locationDisplay = jobLocationDisplay(sj, locale);
					return (
						<Link
							key={sj.id}
							href={href}
							onClick={() =>
								trackJobClick(sj.id, {
									surface: "related_jobs",
									locale,
									source_job_id: currentJobId,
									placement,
									position: index,
								})
							}
							className={`group flex flex-col gap-1.5 rounded-xl border border-border/60 bg-card p-4 transition-colors hover:border-primary/40 hover:bg-accent/50 ${linkRing}`}
						>
							<p className="line-clamp-2 text-sm font-semibold text-foreground group-hover:text-primary">
								{sj.title}
							</p>
							{sj.company ? (
								<p className="truncate text-xs text-muted-foreground">
									{sj.company}
								</p>
							) : null}
							{pay ? (
								<p className="text-xs font-medium text-success dark:text-emerald-400">
									{pay}
								</p>
							) : null}
							<p className="text-xs text-muted-foreground">
								{locationDisplay.text}
							</p>
						</Link>
					);
				})}
			</div>
		</section>
	);
}
